import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  MoreHorizontal,
  Pencil,
  FolderInput,
  UserCheck,
  Trash2,
} from 'lucide-react'
import { Modal } from './ui/Modal'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { ListMoveDialog } from './ListMoveDialog'
import { Button } from './ui/Button'
import { TextField } from './ui/Field'
import { api, extractApiError } from '../lib/api'
import { CONTAINS_LETTER } from '../lib/validators'
import { renameFavoriteListMeta } from '../lib/space-prefs'
import { workspaceMembersResponse } from '../lib/types'
import { useAuthStore } from '../stores/authStore'

const renameSchema = z.object({
  name: z
    .string({ message: 'List Name is required.' })
    .trim()
    .min(1, 'List Name is required.')
    .min(2, 'List Name must be at least 2 characters.')
    .max(100, 'List Name must be at most 100 characters.')
    .regex(CONTAINS_LETTER, 'List Name must contain at least one letter.'),
})

export function ListRowActions({ list, workspaceId }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [selectedAssignee, setSelectedAssignee] = useState(
    list.defaultAssigneeId ?? '',
  )

  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const [coords, setCoords] = useState(null)

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    const r = triggerRef.current?.getBoundingClientRect()
    if (r) setCoords({ top: r.top, left: r.right + 4 })
  }, [open])
  useLayoutEffect(() => {
    if (!open || !coords) return
    const menu = menuRef.current
    const r = triggerRef.current?.getBoundingClientRect()
    if (!menu || !r) return
    const margin = 8
    const viewportH = window.innerHeight
    const viewportW = window.innerWidth
    let top = r.top
    if (top + menu.offsetHeight > viewportH - margin) {
      top = Math.max(margin, viewportH - margin - menu.offsetHeight)
    }
    // Default opens to the RIGHT of the trigger; flip to the LEFT when that
    // would overflow the viewport (trigger near the right edge on mobile),
    // then clamp so it never runs off either edge.
    let left = r.right + 4
    if (left + menu.offsetWidth > viewportW - margin) {
      left = r.left - menu.offsetWidth - 4
    }
    left = Math.max(margin, Math.min(left, viewportW - menu.offsetWidth - margin))
    if (Math.abs(top - coords.top) > 1 || Math.abs(left - coords.left) > 1) {
      setCoords({ top, left })
    }
  }, [open, coords])

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      const t = e.target
      if (!triggerRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const renameForm = useForm({
    resolver: zodResolver(renameSchema),
    defaultValues: { name: list.name },
  })
  useEffect(() => {
    if (renameOpen) renameForm.reset({ name: list.name })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameOpen, list.name])

  const renameMutation = useMutation({
    mutationFn: async (v) => {
      await api.patch(`/spaces/${list.spaceId}/lists/${list.id}`, {
        name: v.name.trim(),
      })
    },
    // Capture the pre-rename name so the Favourites refresh below can tell
    // "this favourite showed the list's own name" (update it) from "it showed
    // the parent Project's name" (leave it) — race-proof via mutation context.
    onMutate: () => ({ previousName: list.name }),
    onSuccess: (_data, v, ctx) => {
      toast.success('List renamed successfully.')
      setRenameOpen(false)
      void qc.invalidateQueries({ queryKey: ['lists', list.spaceId] })
      // Also refresh the OPEN list's singular query so its page heading tracks
      // the rename (the collection invalidation above doesn't cover it).
      void qc.invalidateQueries({ queryKey: ['list', list.spaceId, list.id] })
      // Keep the Favourites sidebar's stored name snapshot in step with the
      // rename — otherwise a favourited list keeps its old name pinned there.
      renameFavoriteListMeta(
        useAuthStore.getState().user?.id,
        list.id,
        ctx?.previousName,
        v.name.trim(),
      )
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields?.name) renameForm.setError('name', { message: fields.name })
      else toast.error(message)
    },
  })

  // Default-assignee picker roster — scoped to who can actually open this
  // Space (the same endpoint the in-Space assignee picker uses). Fetched only
  // when the modal is open. This shares the React Query cache key with
  // SpacePage's assignee picker, so it MUST return the identical
  // `{ data: [...] }` shape (via the same schema parse) — a bare array here
  // would poison that shared entry and crash whichever consumer reads it.
  const membersQuery = useQuery({
    queryKey: ['assignable-members', list.spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${list.spaceId}/assignable-members`)
      return workspaceMembersResponse.parse(res.data)
    },
    enabled: assignOpen,
    staleTime: 60_000,
  })
  const members = membersQuery.data?.data ?? []

  const assignMutation = useMutation({
    mutationFn: async (val) => {
      await api.patch(`/spaces/${list.spaceId}/lists/${list.id}`, {
        defaultAssigneeId: val || null,
      })
    },
    onSuccess: () => {
      toast.success('Default assignee updated.')
      setAssignOpen(false)
      void qc.invalidateQueries({ queryKey: ['lists', list.spaceId] })
      void qc.invalidateQueries({ queryKey: ['list', list.spaceId, list.id] })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      toast.error(fields?.defaultAssigneeId ?? message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/spaces/${list.spaceId}/lists/${list.id}`)
    },
    onSuccess: () => {
      toast.success('List deleted successfully.')
      setConfirmDelete(false)
      void qc.invalidateQueries({ queryKey: ['lists', list.spaceId] })
      // If the user is currently VIEWING the list they just deleted,
      // their URL still points at `/app/spaces/<spaceId>/lists/<id>`
      // — but the API will now 404 every fetch from that page,
      // leaving them on a stale read-only view (the symptom reported
      // by the user). Navigate them up to the parent Space so the
      // sidebar can fall back to its empty-state. The route check is
      // string-based against the current pathname so we only navigate
      // when the deleted list IS the open one — deleting a different
      // list from the sidebar shouldn't yank the user away.
      const path = `/app/spaces/${list.spaceId}/lists/${list.id}`
      if (location.pathname === path) {
        navigate(`/app/spaces/${list.spaceId}`, { replace: true })
      }
    },
    onError: (err) => {
      // 404 here means the list is already gone — the row the user
      // clicked Delete on is stale in the local cache (e.g. another
      // session deleted it, or a previous invalidation was missed).
      // Treat it like a success: dismiss the dialog, refetch the
      // sidebar, and tell the user it's already cleaned up instead
      // of leaving a scary "List not found." toast next to a row
      // that will keep coming back on every retry.
      if (err?.response?.status === 404) {
        toast.success('List was already deleted. Refreshing list.')
        setConfirmDelete(false)
        void qc.invalidateQueries({ queryKey: ['lists', list.spaceId] })
        return
      }
      toast.error(extractApiError(err).message)
    },
  })

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        aria-label="List actions"
        title="List actions"
        className="p-1 rounded text-[color:var(--color-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-2)] cursor-pointer"
      >
        <MoreHorizontal size={14} />
      </button>

      {open && coords
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 60,
              }}
            >
              <Menu
                ref={menuRef}
                onRename={() => {
                  setOpen(false)
                  setRenameOpen(true)
                }}
                onMove={() => {
                  setOpen(false)
                  setMoveOpen(true)
                }}
                onDefaultAssignee={() => {
                  setOpen(false)
                  setSelectedAssignee(list.defaultAssigneeId ?? '')
                  setAssignOpen(true)
                }}
                onDelete={() => {
                  setOpen(false)
                  setConfirmDelete(true)
                }}
              />
            </div>,
            document.body,
          )
        : null}

      <Modal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="Rename List"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setRenameOpen(false)}
              disabled={renameMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={renameForm.handleSubmit((v) => renameMutation.mutate(v))}
              loading={renameMutation.isPending}
            >
              Update
            </Button>
          </>
        }
      >
        <form onSubmit={renameForm.handleSubmit((v) => renameMutation.mutate(v))}>
          <TextField
            label="List Name"
            required
            placeholder="Enter a List Name"
            maxLength={100}
            error={renameForm.formState.errors.name?.message}
            {...renameForm.register('name')}
          />
        </form>
      </Modal>

      <ListMoveDialog
        open={moveOpen}
        list={list}
        workspaceId={workspaceId}
        onClose={() => setMoveOpen(false)}
      />

      <Modal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        title="Default Assignee"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setAssignOpen(false)}
              disabled={assignMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => assignMutation.mutate(selectedAssignee)}
              loading={assignMutation.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-[color:var(--color-ink-muted)]">
          New tasks added to <span className="font-medium text-[color:var(--color-ink)]">{list.name}</span> with
          no assignee are automatically assigned to this person.
        </p>
        {membersQuery.isLoading ? (
          <p className="text-sm text-[color:var(--color-ink-muted)]">Loading members…</p>
        ) : (
          <select
            value={selectedAssignee}
            onChange={(e) => setSelectedAssignee(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
          >
            <option value="">No default assignee</option>
            {members.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.fullName ?? m.emailId ?? m.userId}
              </option>
            ))}
          </select>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete List"
        message={`Are you sure you want to delete "${list.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
      />
    </>
  )
}

const Menu = forwardRef(function Menu(props, ref) {
  return (
    <div
      ref={ref}
      role="menu"
      className="w-56 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)]"
    >
      <p className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
        List Settings
      </p>
      <Group>
        <Item icon={Pencil} label="Rename" onClick={props.onRename} />
        <Item icon={FolderInput} label="Move to" onClick={props.onMove} />
        <Item
          icon={UserCheck}
          label="Default Assignee"
          onClick={props.onDefaultAssignee}
        />
      </Group>
      <Divider />
      <Group>
        <Item icon={Trash2} label="Delete" danger onClick={props.onDelete} />
      </Group>
    </div>
  )
})

function Group({ children }) {
  return <div className="py-1">{children}</div>
}
function Divider() {
  return <div className="border-t border-[color:var(--color-rule)]" aria-hidden="true" />
}

function Item({ icon: Icon, label, danger, onClick }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={
        'w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-[color:var(--color-card-hover)] cursor-pointer ' +
        (danger ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink)]')
      }
    >
      <Icon
        size={14}
        className={
          'shrink-0 ' +
          (danger ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink-muted)]')
        }
      />
      <span className="flex-1 min-w-0 truncate font-medium">{label}</span>
    </button>
  )
}
